import React, { useMemo, useState } from "react";
import { ChevronDown, MapPin, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FormField, Input, Select, Textarea } from "../UI/FormField";
import Button from "../UI/Button";
import {
  CargoLine,
  TransportDetails,
  TransportMode,
} from "../../types/tasks";
import {
  INCOTERM_OPTIONS,
  TRANSPORT_MODES,
} from "../../constants/taskConstants";
import { PACKAGE_TYPES } from "../../constants/formOptions";
import { PartnerAutocomplete } from "../shipments/PartnerAutoComplete";
import type { EntitySnapshot } from "../../types";
import { useAppSelector } from "../../hooks/useStore";
import { isValidPhoneFormat } from "../../utils/phone";

interface TransportErrors {
  pickupCity?: string;
  deliveryCity?: string;
  pickupPhone?: string;
  deliveryPhone?: string;
}

interface Props {
  value: TransportDetails;
  onChange: (patch: Partial<TransportDetails>) => void;
  readonly?: boolean;
  locked?: boolean;
  errors?: TransportErrors;
}

const mkId = () => `cl_${Math.random().toString(36).slice(2, 10)}`;

const RVal: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <span className="text-[13px] text-gray-800 leading-5">
    {children ?? <span className="text-gray-400">—</span>}
  </span>
);

const Section: React.FC<{
  title: string;
  defaultOpen?: boolean;
  tone?: "pickup" | "delivery" | "neutral";
  children: React.ReactNode;
}> = ({ title, defaultOpen = false, tone = "neutral", children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const dotClass =
    tone === "pickup"
      ? "text-green-600"
      : tone === "delivery"
        ? "text-red-600"
        : "text-gray-400";
  return (
    <div className="border border-gray-200 rounded-md bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <MapPin className={`w-4 h-4 ${dotClass}`} />
          <span className="text-[13px] font-semibold text-gray-700 uppercase tracking-wide">
            {title}
          </span>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && <div className="px-3 pb-3 pt-2 border-t border-gray-100">{children}</div>}
    </div>
  );
};

export const TaskFormTransport: React.FC<Props> = ({ value, onChange, readonly, locked, errors }) => {
  const { t } = useTranslation("tasks");

  // Countries from Redux for the country dropdown
  const rawCountries = useAppSelector((state) => state.company.countries);
  const countryOptions = useMemo(
    () => (rawCountries ?? []).flat().map((c: { _id: string; name: string }) => ({ value: c._id, label: c.name })),
    [rawCountries],
  );

  const applySnapshot = (snapshot: EntitySnapshot, side: "pickup" | "delivery") => {
    const country = snapshot.address?.country;
    const countryId =
      typeof country === "object" && country !== null
        ? (country as { _id: string })._id
        : (country as string) || undefined;
    const prefix = side;
    const addressLine = [snapshot.address?.street, snapshot.address?.city]
      .filter(Boolean)
      .join(", ");
    onChange({
      [`${prefix}Company`]:    snapshot.companyName,
      [`${prefix}CompanyId`]:  snapshot._id,
      [`${prefix}Address`]:    addressLine,
      [`${prefix}RegCode`]:    snapshot.registerationCode || undefined,
      [`${prefix}Phone`]:      snapshot.phoneNumber || undefined,
      [`${prefix}Email`]:      snapshot.emailAddress || undefined,
      [`${prefix}Street`]:     snapshot.address?.street || undefined,
      [`${prefix}City`]:       snapshot.address?.city || undefined,
      [`${prefix}State`]:      snapshot.address?.state || undefined,
      [`${prefix}PostalCode`]: snapshot.address?.postalCode || undefined,
      [`${prefix}Country`]:    countryId,
    } as Partial<TransportDetails>);
  };

  const clearSide = (side: "pickup" | "delivery") => {
    const p = side;
    onChange({
      [`${p}Company`]: undefined, [`${p}CompanyId`]: undefined,
      [`${p}Address`]: undefined, [`${p}RegCode`]: undefined,
      [`${p}Phone`]: undefined,   [`${p}Email`]: undefined,
      [`${p}Street`]: undefined,  [`${p}City`]: undefined,
      [`${p}State`]: undefined,   [`${p}PostalCode`]: undefined,
      [`${p}Country`]: undefined,
    } as Partial<TransportDetails>);
  };

  const today = new Date().toISOString().split("T")[0];

  const pickupDateErr =
    !readonly && !locked && value.pickupDate && value.pickupDate < today
      ? t("transport.validation.pickupPast")
      : undefined;

  const deliveryDateErr =
    !readonly && !locked && value.deliveryDate
      ? value.pickupDate && value.deliveryDate < value.pickupDate
        ? t("transport.validation.deliveryBeforePickup")
        : value.deliveryDate < today
          ? t("transport.validation.deliveryPast")
          : undefined
      : undefined;

  const cargoLines: CargoLine[] = (value.cargoLines ?? []).filter(
    (c): c is CargoLine => c != null && typeof c === "object",
  );

  const updateCargo = (id: string, patch: Partial<CargoLine>) => {
    onChange({ cargoLines: cargoLines.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
  };
  const addCargo = () => onChange({ cargoLines: [...cargoLines, { id: mkId() }] });
  const removeCargo = (id: string) => onChange({ cargoLines: cargoLines.filter((c) => c.id !== id) });

  const th = "px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider";
  const td = "px-2 py-1 align-top";

  /** Full pickup/delivery section matching the shipment form layout */
  const renderLocationSection = (side: "pickup" | "delivery") => {
    const company    = side === "pickup" ? value.pickupCompany    : value.deliveryCompany;
    const dateVal    = side === "pickup" ? value.pickupDate       : value.deliveryDate;
    const regCode    = side === "pickup" ? value.pickupRegCode    : value.deliveryRegCode;
    const phone      = side === "pickup" ? value.pickupPhone      : value.deliveryPhone;
    const email      = side === "pickup" ? value.pickupEmail      : value.deliveryEmail;
    const street     = side === "pickup" ? value.pickupStreet     : value.deliveryStreet;
    const city       = side === "pickup" ? value.pickupCity       : value.deliveryCity;
    const state      = side === "pickup" ? value.pickupState      : value.deliveryState;
    const postalCode = side === "pickup" ? value.pickupPostalCode : value.deliveryPostalCode;
    const countryId  = side === "pickup" ? value.pickupCountry    : value.deliveryCountry;
    const dateErr    = side === "pickup" ? pickupDateErr          : deliveryDateErr;
    const hasData    = !!(company || city || street);

    const field = (
      key: string,
      label: string,
      val: string | undefined,
      placeholder: string,
      fieldError?: string,
      extra?: React.ReactNode,
    ) =>
      readonly ? (
        <FormField key={key} layout="inline" size="sm" label={label}>
          <RVal>{val}</RVal>
        </FormField>
      ) : (
        <FormField key={key} layout="inline" size="sm" label={label} error={fieldError}>
          {extra ?? (
            <Input
              type="text"
              size="sm"
              value={val ?? ""}
              readOnly={locked}
              placeholder={locked ? "" : placeholder}
              error={!!fieldError}
              onChange={(e) =>
                onChange({ [`${side}${key}`]: e.target.value || undefined } as Partial<TransportDetails>)
              }
            />
          )}
        </FormField>
      );

    return (
      <Section title={t(`transport.${side}`)} tone={side} defaultOpen={hasData}>
        <div className="space-y-1">
          {/* Company — searchable dropdown with snapshot */}
          <FormField layout="inline" size="sm" label={t("transport.company")}>
            {readonly ? (
              <RVal>{company}</RVal>
            ) : (
              <PartnerAutocomplete
                value={company ?? ""}
                onChange={(v) =>
                  onChange({
                    [`${side}Company`]: v || undefined,
                    [`${side}CompanyId`]: undefined,
                  } as Partial<TransportDetails>)
                }
                onSnapshotSelect={(s) => applySnapshot(s, side)}
                onClear={() => clearSide(side)}
                placeholder={t("transport.companyPlaceholder")}
                addresses={true}
                type={side}
                size="sm"
                disabled={locked}
              />
            )}
          </FormField>

          {/* Reg. Code */}
          {field("RegCode", t("transport.regCode"), regCode, t("transport.regCodePlaceholder"))}

          {field(
            "Phone",
            t("transport.phone"),
            phone,
            t("transport.phonePlaceholder"),
            errors?.[`${side}Phone`] ??
              (isValidPhoneFormat(phone)
                ? undefined
                : t("transport.phoneInvalid")),
          )}

          {/* Email */}
          {field("Email", t("transport.email"), email, t("transport.emailPlaceholder"))}

          {/* Date */}
          <FormField layout="inline" size="sm" label={t("transport.date")} error={dateErr}>
            {readonly ? (
              <RVal>{dateVal}</RVal>
            ) : (
              <Input
                type="date"
                size="sm"
                min={locked ? undefined : side === "pickup" ? today : (value.pickupDate ?? today)}
                value={dateVal ?? ""}
                error={!!dateErr}
                readOnly={locked}
                onChange={(e) =>
                  onChange({ [`${side}Date`]: e.target.value || undefined } as Partial<TransportDetails>)
                }
              />
            )}
          </FormField>

          {/* Street */}
          {field("Street", t("transport.street"), street, t("transport.streetPlaceholder"))}

          {/* City ← required for shipment creation */}
          {field(
            "City",
            t("transport.city"),
            city,
            t("transport.cityPlaceholder"),
            side === "pickup" ? errors?.pickupCity : errors?.deliveryCity,
          )}

          {/* State */}
          {field("State", t("transport.state"), state, t("transport.statePlaceholder"))}

          {/* Postal Code */}
          {field("PostalCode", t("transport.postalCode"), postalCode, t("transport.postalCodePlaceholder"))}

          {/* Country dropdown */}
          <FormField layout="inline" size="sm" label={t("transport.country")}>
            {readonly ? (
              <RVal>
                {countryOptions.find((c) => c.value === countryId)?.label ?? countryId}
              </RVal>
            ) : (
              <Select
                size="sm"
                value={countryId ?? ""}
                disabled={locked}
                onChange={(e) =>
                  onChange({ [`${side}Country`]: e.target.value || undefined } as Partial<TransportDetails>)
                }
              >
                <option value="">—</option>
                {countryOptions.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </Select>
            )}
          </FormField>
        </div>
      </Section>
    );
  };

  return (
    <div className="space-y-2">
      {renderLocationSection("pickup")}
      {renderLocationSection("delivery")}

      {/* Transport Type */}
      <Section title={t("transport.transportType")} defaultOpen={!!value.transportType}>
        <FormField layout="inline" size="sm" label={t("transport.transportType")}>
          {readonly ? (
            <RVal>
              {value.transportType
                ? t(`transport.transportTypeOptions.${value.transportType}`)
                : undefined}
            </RVal>
          ) : (
            <Select
              size="sm"
              value={value.transportType ?? ""}
              disabled={locked}
              onChange={(e) =>
                onChange({ transportType: (e.target.value || undefined) as TransportMode | undefined })
              }
            >
              <option value="">—</option>
              {TRANSPORT_MODES.map((m) => (
                <option key={m} value={m}>{t(`transport.transportTypeOptions.${m}`)}</option>
              ))}
            </Select>
          )}
        </FormField>
      </Section>

      {/* Incoterm */}
      <Section title={t("transport.incoterm")} defaultOpen={!!value.incoterm}>
        <div className="space-y-1">
          <FormField layout="inline" size="sm" label={t("transport.incoterm")}>
            {readonly ? (
              <RVal>{value.incoterm}</RVal>
            ) : (
              <Select
                size="sm"
                value={value.incoterm ?? ""}
                disabled={locked}
                onChange={(e) => onChange({ incoterm: e.target.value || undefined })}
              >
                <option value="">—</option>
                {INCOTERM_OPTIONS.map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField layout="inline" size="sm" label={t("transport.incotermLocation")}>
            {readonly ? (
              <RVal>{value.incotermLocation}</RVal>
            ) : (
              <Input
                type="text"
                size="sm"
                value={value.incotermLocation ?? ""}
                readOnly={locked}
                onChange={(e) => onChange({ incotermLocation: e.target.value || undefined })}
                placeholder={t("transport.incotermLocationPlaceholder")}
              />
            )}
          </FormField>
        </div>
      </Section>

      {/* Cargo */}
      <Section title={t("transport.cargo")} defaultOpen={cargoLines.length > 0}>
        {cargoLines.length > 0 &&
          (() => {
            const totalQty    = cargoLines.reduce((s, l) => s + (l.quantity  ?? 0), 0);
            const totalWeight = cargoLines.reduce((s, l) => s + (l.weightKg ?? 0), 0);
            const totalLDM    = cargoLines.reduce((s, l) => s + (l.ldm      ?? 0), 0);
            const totalVol    = cargoLines.reduce((s, l) => s + (l.volumeM3 ?? 0), 0);

            if (readonly) {
              return (
                <div className="border border-gray-200 rounded-md overflow-hidden mb-2">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 text-nowrap border-b border-gray-200">
                        <tr>
                          <th className={th}>{t("transport.cargoQuantity")}</th>
                          <th className={th}>{t("transport.cargoUnit")}</th>
                          <th className={th}>Weight (kg)</th>
                          <th className={th}>Volume (m³)</th>
                          <th className={th}>LDM</th>
                          <th className={th}>{t("transport.cargoDescription")}</th>
                          <th className={th}>Marks/Numbers</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {cargoLines.map((line) => (
                          <tr key={line.id}>
                            <td className={`${td} text-gray-800`}>{line.quantity ?? "—"}</td>
                            <td className={`${td} text-gray-800`}>{line.unit ?? "—"}</td>
                            <td className={`${td} text-gray-800`}>{line.weightKg ?? "—"}</td>
                            <td className={`${td} text-gray-800`}>{line.volumeM3 ?? "—"}</td>
                            <td className={`${td} text-gray-800`}>{line.ldm ?? "—"}</td>
                            <td className={`${td} text-gray-800`}>{line.description ?? "—"}</td>
                            <td className={`${td} text-gray-800`}>{line.marks ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
                    <span className="font-semibold text-gray-600 uppercase tracking-wide shrink-0">Totals</span>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 ml-auto">
                      <div><span className="text-gray-500">Qty</span> <span className="font-semibold text-gray-900">{totalQty}</span></div>
                      <div><span className="text-gray-500">Weight</span> <span className="font-semibold text-gray-900">{totalWeight.toFixed(2)} kg</span></div>
                      <div><span className="text-gray-500">LDM</span> <span className="font-semibold text-gray-900">{totalLDM.toFixed(3)}</span></div>
                      <div><span className="text-gray-500">Volume</span> <span className="font-semibold text-gray-900">{totalVol.toFixed(3)} m³</span></div>
                    </div>
                  </div>
                </div>
              );
            }

            // Volume auto-calc from dimensions (only when volume field is empty/zero)
            const handleDim = (id: string, patch: Partial<CargoLine>, current: CargoLine) => {
              const merged = { ...current, ...patch };
              const l = Number(merged.length ?? 0);
              const w = Number(merged.width  ?? 0);
              const h = Number(merged.height ?? 0);
              if (l > 0 && w > 0 && h > 0) {
                const volEmpty = !current.volumeM3 || Number(current.volumeM3) === 0;
                if (volEmpty) patch.volumeM3 = parseFloat(((l * w * h) / 1_000_000).toFixed(3));
              }
              updateCargo(id, patch);
            };

            return (
              <div className="border border-gray-200 rounded-md overflow-hidden mb-2">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 text-nowrap border-b border-gray-200">
                      <tr>
                        <th className={th}>{t("transport.cargoQuantity")}</th>
                        <th className={th}>{t("transport.cargoUnit")}</th>
                        <th className={th}>Weight (kg)</th>
                        <th className={th}>Volume (m³)</th>
                        {!locked && (
                          <th className={th}>
                            Dimensions <span className="font-normal text-gray-400">(L×W×H cm)</span>
                          </th>
                        )}
                        <th className={th}>LDM</th>
                        <th className={th}>{t("transport.cargoDescription")}</th>
                        <th className={th}>Marks/Numbers</th>
                        {!locked && <th className={`${th} w-8`} />}
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {cargoLines.map((line) => (
                        <tr key={line.id} className="border-b border-gray-100 last:border-0">
                          <td className={td}>
                            <Input type="number" size="sm" min={0} style={{ width: "72px" }}
                              readOnly={locked} value={line.quantity ?? ""}
                              onChange={(e) => updateCargo(line.id, { quantity: e.target.value ? Number(e.target.value) : undefined })}
                            />
                          </td>
                          <td className={td}>
                            <Select size="sm" value={line.unit ?? ""} disabled={locked}
                              onChange={(e) => updateCargo(line.id, { unit: e.target.value || undefined })}>
                              <option value="">—</option>
                              {PACKAGE_TYPES.map((p) => (
                                <option key={p.value} value={p.value}>{p.label}</option>
                              ))}
                            </Select>
                          </td>
                          <td className={td}>
                            <Input type="number" size="sm" min={0} step="0.01" style={{ width: "80px" }}
                              readOnly={locked} value={line.weightKg ?? ""}
                              onChange={(e) => updateCargo(line.id, { weightKg: e.target.value ? Number(e.target.value) : undefined })}
                            />
                          </td>
                          <td className={td}>
                            {/* Free input — also auto-calculated from L×W×H when empty */}
                            <Input type="number" size="sm" min={0} step="0.01" style={{ width: "80px" }}
                              readOnly={locked} value={line.volumeM3 ?? ""}
                              onChange={(e) => updateCargo(line.id, { volumeM3: e.target.value ? Number(e.target.value) : undefined })}
                            />
                          </td>
                          {!locked && (
                            <td className={td}>
                              <div className="flex items-center gap-0.5">
                                {(["length", "width", "height"] as const).map((dim, i) => (
                                  <React.Fragment key={dim}>
                                    {i > 0 && <span className="text-gray-400">×</span>}
                                    <Input
                                      type="number" size="sm" min={0} step="0.1"
                                      style={{ width: "52px" }}
                                      value={line[dim] ?? ""}
                                      placeholder={dim[0].toUpperCase()}
                                      onChange={(e) =>
                                        handleDim(
                                          line.id,
                                          { [dim]: e.target.value ? Number(e.target.value) : undefined },
                                          line,
                                        )
                                      }
                                    />
                                  </React.Fragment>
                                ))}
                              </div>
                            </td>
                          )}
                          <td className={td}>
                            {/* LDM — pure free input, no auto-calculation */}
                            <Input type="number" size="sm" min={0} step="0.01" style={{ width: "72px" }}
                              readOnly={locked} value={line.ldm ?? ""}
                              onChange={(e) => updateCargo(line.id, { ldm: e.target.value ? Number(e.target.value) : undefined })}
                            />
                          </td>
                          <td className={td}>
                            <Input type="text" size="sm" style={{ width: "150px" }}
                              readOnly={locked} value={line.description ?? ""}
                              onChange={(e) => updateCargo(line.id, { description: e.target.value || undefined })}
                              placeholder={locked ? "" : "Description"}
                            />
                          </td>
                          <td className={td}>
                            <Input type="text" size="sm" style={{ width: "120px" }}
                              readOnly={locked} value={line.marks ?? ""}
                              onChange={(e) => updateCargo(line.id, { marks: e.target.value || undefined })}
                              placeholder={locked ? "" : "Marks/numbers"}
                            />
                          </td>
                          {!locked && (
                            <td className={td}>
                              <button type="button" onClick={() => removeCargo(line.id)}
                                className="p-1 text-gray-400 hover:text-red-600 transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center text-[12px]">
                  <span className="font-semibold text-gray-600 uppercase tracking-wide">Totals</span>
                  <div className="flex gap-6">
                    <div><span className="text-gray-500">Qty</span> <span className="font-semibold text-gray-900">{totalQty}</span></div>
                    <div><span className="text-gray-500">Weight</span> <span className="font-semibold text-gray-900">{totalWeight.toFixed(2)} kg</span></div>
                    <div><span className="text-gray-500">LDM</span> <span className="font-semibold text-gray-900">{totalLDM.toFixed(3)}</span></div>
                    <div><span className="text-gray-500">Volume</span> <span className="font-semibold text-gray-900">{totalVol.toFixed(3)} m³</span></div>
                  </div>
                </div>
              </div>
            );
          })()}
        {!readonly && !locked && (
          <Button type="button" variant="outline" size="sm"
            icon={<Plus className="w-3.5 h-3.5" />} onClick={addCargo}>
            {t("transport.cargoAddLine")}
          </Button>
        )}
      </Section>

      {/* Transport Remarks */}
      <Section title={t("transport.remarks")} defaultOpen={!!value.transportRemarks}>
        <FormField layout="inline" size="sm" label={t("transport.remarks")}>
          {readonly ? (
            <p className="text-[13px] text-gray-700 whitespace-pre-wrap min-h-[2rem] leading-relaxed">
              {value.transportRemarks || <span className="text-gray-400">—</span>}
            </p>
          ) : (
            <Textarea
              size="sm"
              rows={2}
              value={value.transportRemarks ?? ""}
              readOnly={locked}
              onChange={(e) => onChange({ transportRemarks: e.target.value || undefined })}
              placeholder={locked ? "" : t("transport.remarksPlaceholder")}
            />
          )}
        </FormField>
      </Section>
    </div>
  );
};
